import React, { useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { IconArrowLeft } from '@tabler/icons-react'
import { isNil } from 'lodash'

import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Loader } from '@/components/loaders/Loader'
import { PageHeader } from '@/components/headers/PageHeader'

import { fetchOrgScorecards } from '@/reducers/costimizer/costimizer.actions'
import { selectOrg } from '@/reducers/costimizer/costimizer.redux'

import { MigrationCell } from './components/MigrationCell'
import { ScorecardResults } from './components/ScorecardResults'
import { scorecardOpportunity } from './Costimizer.helpers'

import './Costimizer.scss'

/**
 * One scorecard's prompts, and what a migration found for each.
 *
 * A page rather than an expanded row on the org list: this is a table, and nesting a table inside another
 * one means inheriting its cell styling. Doing this here keeps `AdvancedTable` — which 86 files use —
 * untouched, and means a scorecard's results have a URL.
 *
 * ⚠ The org id is in the path so the page can load on its own. There is no endpoint for a single scorecard;
 * the list comes per org, and this picks its own out of it. Arriving from the org page finds it already in
 * the store and renders without a fetch.
 */
export const CostimizerScorecardPage = () => {
  const dispatch = useDispatch()
  const { organizationId, scorecardConfigId } = useParams()
  const { loading, error, scorecards } = useSelector(selectOrg(organizationId))

  useEffect(() => {
    dispatch(fetchOrgScorecards(organizationId))
  }, [dispatch, organizationId])

  // ⚠ MATCHED ON ANY VERSION OF THE SCORECARD, not on the id in the URL. `scorecard_configs` is
  // insert-only, so applying a migration from this very page mints a new id — and matching the URL's id
  // against today's turned a successful apply into "Scorecard not found". The row that comes back is the
  // current version, which is what a new run or a second apply must be aimed at.
  const found = scorecards.find((entry) =>
    entry.configIds.some((id) => String(id) === String(scorecardConfigId))
  )
  const scorecard = isNil(found) ? null : { ...found, ...scorecardOpportunity(found.subjects) }

  const backToOrg = (
    <Link to={`/costimizer/${organizationId}`} className="balto-button icon">
      <IconArrowLeft />
      All scorecards
    </Link>
  )

  if (error) {
    return (
      <>
        <PageHeader title="Scorecard" actions={backToOrg} />
        <ErrorMessage content="Could not load this organization's scorecards." />
      </>
    )
  }

  if (loading) {
    return (
      <>
        <PageHeader title="Scorecard" actions={backToOrg} />
        <Loader centered label="Loading scorecard" />
      </>
    )
  }

  // An empty state rather than a toast: a link to a scorecard that has since been deleted, or a mistyped id,
  // is an expected way to arrive here rather than something gone wrong.
  if (isNil(scorecard)) {
    return (
      <PageHeader
        data-testid="costimizer-scorecard-page"
        title="Scorecard not found"
        description={`Nothing with id ${scorecardConfigId} on org ${organizationId}`}
        actions={backToOrg}
      />
    )
  }

  return (
    <>
      <PageHeader
        data-testid="costimizer-scorecard-page"
        title={scorecard.name}
        description={`Org ${organizationId} · Quality prompts`}
        actions={
          <span className="flex-align-center medium-gap">
            <MigrationCell
              scorecardConfigId={scorecard.scorecardConfigId}
              fullyMigrated={scorecard.fullyMigrated}
              nMigratable={scorecard.nMigratable}
              perPromptMinutes={scorecard.perPromptMinutes}
              serverRun={scorecard.run}
            />
            {backToOrg}
          </span>
        }
      />

      <ScorecardResults scorecard={scorecard} />
    </>
  )
}
